// Wallet page — balance + recharge/withdraw flows + transaction
// ledger + per-game history.
//
// Phase 2 added:
//   - Active "Add money" / "Withdraw" buttons that open the wizard
//     modals (RechargeFlow / WithdrawalFlow).
//   - "Pending requests" panel above the ledger showing the player's
//     in-flight recharges + withdrawals with state badges.
//   - New ledger kind labels for offer_credit, withdrawal_hold,
//     withdrawal_refund, agent_topup_in, agent_co_fund.
//
// Data flow:
//   * Balance lives in the shared wallet store (`useWallet`) so every
//     mount reflects the same number; live updates come via WS from
//     each game's engine + after a recharge/withdrawal submit.
//   * Transaction ledger comes from GET /api/me/ledger.
//   * Per-game win/loss summary comes from GET /api/me/game-summary.
//   * Pending payment requests from GET /api/me/payments/recharges|withdrawals
//     filtered to {pending, approved} states (the player-active rows).
import { useEffect, useState, useCallback } from 'react';
import { useWallet, refreshWallet } from '@baaligames/shared-ui/lib/wallet';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';
import RechargeFlow from '@baaligames/shared-ui/components/RechargeFlow';
import WithdrawalFlow from '@baaligames/shared-ui/components/WithdrawalFlow';
import ProofImageViewer, { type ProofState } from '@baaligames/shared-ui/components/ProofImageViewer';
import ProofImagePicker from '@baaligames/shared-ui/components/ProofImagePicker';

type LedgerRow = {
  id: number;
  game: string | null;
  kind: string;
  amount: number;
  note: string | null;
  actor_admin_id: number | null;
  balance_after: number | null;
  session_id: number | null;
  created_at: string;
  // Withdrawal-hold enrichment from the LEFT JOIN on withdrawal_requests
  // via hold_ledger_id. Null on every other ledger kind. When the
  // request has been settled, the UI swaps the row's label so the
  // player doesn't see "Withdrawal hold" forever after their
  // withdrawal was approved/rejected.
  hold_request_id?: number | null;
  hold_request_state?: 'pending' | 'approved' | 'rejected' | 'paid' | 'expired' | null;
  hold_request_amount?: number | null;
  hold_approved_amount?: number | null;
  hold_paid_transaction_id?: string | null;
  hold_paid_at?: string | null;
  hold_decision_note?: string | null;
  // Phase 18 — payout proof from the agent/admin who marked this paid.
  // When set, the player sees a "View screenshot" button on the
  // History row. Backend gates the GET (72h window + 2-view cap).
  hold_payout_proof_url?: string | null;
  hold_payout_proof_uploaded_at?: string | null;
};
type LedgerWindow = { window_days: number; latest_at: string; cutoff_at: string };
type LedgerPage = {
  rows: LedgerRow[];
  total: number;
  limit: number;
  offset: number;
  // Phase R7 follow-up (2026-05-13): the API now returns a rolling
  // window anchored on the player's MOST RECENT transaction
  // (latest_at). Rows older than (latest_at − window_days) are excluded.
  // `total` already reflects this filter so pagination stays accurate.
  // Null when the player has no transactions yet.
  window?: LedgerWindow | null;
};

type GameSummary = {
  id: string;
  displayName: string;
  bets: number;
  wins: number;
  refunds: number;
  fees: number;
  net: number;
  matches: number;
};

// Phase 18 — proof columns flow through every request row.
type ProofColumns = {
  proof_image_url: string | null;
  proof_uploaded_at: string | null;
  payout_proof_url: string | null;
  payout_proof_uploaded_at: string | null;
  payout_proof_view_count: number;
};
type RechargeRow = ProofColumns & {
  id: number;
  amount: number;
  transaction_id: string;
  state: 'pending'|'approved'|'rejected'|'expired';
  created_at: string;
  decision_note: string | null;
};
type WithdrawalRow = ProofColumns & {
  id: number;
  amount: number;
  upi_id: string;
  state: 'pending'|'approved'|'rejected'|'paid'|'expired';
  created_at: string;
  paid_transaction_id: string | null;
  decision_note: string | null;
};

// Ledger row metadata. Labels are looked up via t('ledger.kind.<kind>')
// at render time so the activity list flips language with the rest of
// the UI. The tone + icon stay here because they're presentation-only.
const KIND_META: Record<string, { tone: 'pos' | 'neg' | 'neutral'; icon: string }> = {
  bet:                { tone: 'neg',     icon: 'sports_esports' },
  win:                { tone: 'pos',     icon: 'emoji_events' },
  refund:             { tone: 'pos',     icon: 'undo' },
  forfeit:            { tone: 'neg',     icon: 'block' },
  fee:                { tone: 'neutral', icon: 'receipt_long' },
  recharge:           { tone: 'pos',     icon: 'add_circle' },
  admin_credit:       { tone: 'pos',     icon: 'add_circle' },
  admin_debit:        { tone: 'neg',     icon: 'remove_circle' },
  referral_bonus:     { tone: 'pos',     icon: 'card_giftcard' },
  signup_bonus:       { tone: 'pos',     icon: 'redeem' },
  offer_credit:       { tone: 'pos',     icon: 'redeem' },
  withdrawal_hold:    { tone: 'neg',     icon: 'lock' },
  withdrawal_refund:  { tone: 'pos',     icon: 'undo' },
  agent_topup_in:     { tone: 'pos',     icon: 'support_agent' },
  agent_co_fund:      { tone: 'pos',     icon: 'handshake' },
  turnover_reward:    { tone: 'pos',     icon: 'cycle' },
};

function fmt(n: number): string {
  return Math.floor(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export default function Wallet() {
  const t = useT();
  const { user } = useAuth();
  const { balance, loading: balLoading, error: balError } = useWallet();
  const [summary, setSummary] = useState<GameSummary[] | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [ledger, setLedger] = useState<LedgerPage | null>(null);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  // How many days of history to load. Starts at the recent 6-day window;
  // "Show older" expands it so the player can reach their full history
  // (2026-08-06 UX sweep — the feed was capped at 6 days with no way past).
  const [windowDays, setWindowDays] = useState(6);
  const LIMIT = 25;

  // Pending request panels.
  const [pendingRecharges, setPendingRecharges] = useState<RechargeRow[]>([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState<WithdrawalRow[]>([]);

  // Modal state.
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const data = await api.get<{ games: GameSummary[] }>('/me/game-summary');
      setSummary(data.games || []);
      setSummaryError(null);
    } catch (e) {
      setSummaryError((e as ApiError).message || 'Failed to load');
    }
  }, []);

  const loadLedger = useCallback(async (off: number) => {
    setLedgerLoading(true);
    try {
      // Phase R7 follow-up: cap the activity feed to the most recent
      // 6 days of the player's own activity (anchored on their latest
      // transaction, not "today"). The backend handles the cutoff math
      // and returns metadata in `data.window` for the notice banner.
      const data = await api.get<LedgerPage>(`/me/ledger?limit=${LIMIT}&offset=${off}&windowDays=${windowDays}`);
      setLedger(data);
      setLedgerError(null);
    } catch (e) {
      setLedgerError((e as ApiError).message || 'Failed to load');
    } finally {
      setLedgerLoading(false);
    }
  }, [windowDays]);

  // Expand the history window (6 → 30 → 90 → 365 → all) and jump back to
  // the first page of the newly-widened feed.
  const showOlder = useCallback(() => {
    setOffset(0);
    setWindowDays((d) => (d < 30 ? 30 : d < 90 ? 90 : d < 365 ? 365 : 3650));
  }, []);
  const windowIsMax = windowDays >= 3650;

  // Pending payments — recharges in 'pending'/'approved' (the latter is
  // for the brief window where admin has approved but not yet credited);
  // withdrawals in 'pending'/'approved' (approved = admin will pay soon).
  const loadPending = useCallback(async () => {
    try {
      const [pr, pw] = await Promise.all([
        api.get<{ rows: RechargeRow[] }>('/me/payments/recharges?state=pending&limit=10'),
        api.get<{ rows: WithdrawalRow[] }>('/me/payments/withdrawals?state=pending&limit=10'),
      ]);
      setPendingRecharges(pr.rows);
      setPendingWithdrawals(pw.rows);
    } catch {
      // Non-blocking: pending panel just stays empty on error.
    }
  }, []);

  useEffect(() => {
    void refreshWallet();
    void loadSummary();
    void loadPending();
  }, [loadSummary, loadPending]);

  useEffect(() => { void loadLedger(offset); }, [loadLedger, offset]);

  // Refresh-after-submit hook for the modals.
  function reloadAll() {
    void refreshWallet();
    void loadPending();
    void loadLedger(offset);
  }

  const hasPending = pendingRecharges.length + pendingWithdrawals.length > 0;

  return (
    <div className="space-y-3">
      {/* ─── Balance card — Phase R7 follow-up (2026-05-13) ───────────
          INVERSE of the homepage WalletHero. Both cards use the same
          two surfaces but swap them per theme:
            * Home /         light = cyan  · dark = orange
            * Wallet /wallet light = orange · dark = cyan
          So switching the theme produces a noticeable identity flip
          between the two pages — the dashboard balance card stays
          one colour family per mode, the wallet detail page stays
          the other. Text is the same dark navy on both surfaces
          because both gradients are LIGHT enough that navy reads. */}
      <section className="relative overflow-hidden rounded-3xl border
                          border-brand-300/50 dark:border-cyan-400/30 shadow-xl">
        {/* Light: warm orange (mirrors homepage WalletHero DARK). */}
        <div
          className="block dark:hidden absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #FFD9A8 0%, #F4A24A 35%, #F07E33 65%, #E07F1F 100%)',
          }}
          aria-hidden
        />
        {/* Dark: cyan ocean (mirrors homepage WalletHero LIGHT). */}
        <div
          className="hidden dark:block absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #E0F7FA 0%, #8FFFFF 28%, #62F4F7 65%, #23D4D8 100%)',
          }}
          aria-hidden
        />
        {/* Top-right white highlight — works on both gradients
            (orange gets a gloss, cyan gets a screen-blended pop). */}
        <div
          className="absolute -top-12 -right-10 w-44 h-44 rounded-full opacity-60 mix-blend-screen"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,.55), transparent 65%)' }}
          aria-hidden
        />
        {/* Bottom-left halo — same swap logic as the homepage card,
            mirrored: orange halo on the cyan dark surface (warm × cool
            contrast), subtle white halo on the orange light surface. */}
        <div
          className="block dark:hidden absolute -bottom-14 -left-12 w-56 h-56 rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,.4), transparent 65%)' }}
          aria-hidden
        />
        <div
          className="hidden dark:block absolute -bottom-14 -left-12 w-56 h-56 rounded-full opacity-50"
          style={{ background: 'radial-gradient(circle, rgba(240,126,51,.4), transparent 65%)' }}
          aria-hidden
        />

        <div className="relative px-5 py-5">
          {/* All text is dark navy in BOTH modes — high contrast on
              the orange surface AND the cyan surface (both are
              LIGHT-toned), matching the homepage WalletHero approach. */}
          <div className="text-[10px] tracking-[.32em] uppercase font-bold text-arena-900">
            {t('wallet.balance')}
          </div>
          <div className="text-3xl font-extrabold mt-1 tabular-nums flex items-baseline gap-1 text-arena-900">
            <img src="/player/coin.png" alt="" aria-hidden draggable={false}
                 className="w-7 h-7 -ml-0.5 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]" />
            {balLoading ? '…' : fmt(balance)}
          </div>
          <div className="text-[11px] mt-1 text-arena-700">
            {t('wallet.balance.note')}
          </div>
          {balError && (
            <div className="text-xs mt-1 px-2 py-1 rounded
                            bg-arena-900/15 border border-arena-900/30
                            text-arena-900">
              {balError}
            </div>
          )}
        </div>
      </section>

      {/* Active action buttons (replaces the previous disabled placeholders) */}
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => setRechargeOpen(true)}
          className="inline-flex items-center justify-center gap-1.5 h-11 rounded-full
                     font-bold text-[13px] tracking-wider uppercase text-white
                     bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                     border border-white/20 active:scale-[0.98] transition"
          style={{ boxShadow: '0 10px 22px -10px rgba(240,126,51,.7), inset 0 1px 0 rgba(255,255,255,.35)' }}>
          <Icon name="add_circle" size={18} filled /> {t('wallet.addMoney')}
        </button>
        <button onClick={() => setWithdrawOpen(true)}
          /* Gate on the REAL ₹200 minimum withdrawal (WithdrawalFlow MIN_AMOUNT),
             not ₹10 (2026-08-06 UX fix). Before, a player with ₹10–199 could
             open the withdraw wizard but never submit — a dead-end. */
          disabled={balance < 200}
          title={balance < 200 ? t('wallet.withdraw.minBalance') : undefined}
          className="inline-flex items-center justify-center gap-1.5 h-11 rounded-full
                     font-bold text-[13px] tracking-wider uppercase
                     text-cyan-700 dark:text-cyan-300
                     bg-cyan-50 dark:bg-cyan-400/10
                     border border-cyan-300 dark:border-cyan-400/40
                     hover:bg-cyan-100 dark:hover:bg-cyan-400/20
                     active:scale-[0.98] transition disabled:opacity-50">
          <Icon name="account_balance_wallet" size={18} filled /> {t('wallet.withdraw')}
        </button>
      </div>

      {/* ─── Saved payout details (read-only) ─────────────────────────
          Moved here from the Profile page (2026-08-08 UX) — a saved UPI is
          payment info, so it belongs with the money, right next to Withdraw,
          not inside "personal details". It's read-only: the withdrawal flow
          validates + saves the canonical UPI on a successful payout. */}
      <section className="card p-4 space-y-2">
        <header className="flex items-center gap-2">
          <Icon name="account_balance" filled className="text-cyan-600 dark:text-cyan-300" />
          <h3 className="font-bold">{t('wallet.payout.title')}</h3>
        </header>
        <div className="rounded-xl bg-arena-50/70 dark:bg-arena-900/40
                        border border-arena-200 dark:border-arena-600/40
                        px-3 py-2.5 flex items-center gap-2">
          <Icon
            name={user?.saved_upi_id ? 'bookmark_added' : 'bookmark_border'}
            size={18}
            filled
            className={user?.saved_upi_id
              ? 'text-emerald-600 dark:text-emerald-400 shrink-0'
              : 'text-arena-400 dark:text-neon-200/50 shrink-0'}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/60">
              {t('wallet.payout.upiLabel')}
            </div>
            <div className={`font-mono text-sm ${user?.saved_upi_id
              ? 'text-arena-800 dark:text-neon-100 truncate'
              : 'text-arena-400 dark:text-neon-200/50 italic'}`}>
              {user?.saved_upi_id || t('wallet.payout.none')}
            </div>
          </div>
        </div>
        <p className="text-[11px] text-arena-500 dark:text-neon-200/60 leading-snug">
          {t('wallet.payout.hint')}
        </p>
      </section>

      {/* ─── Pending requests panel ───────────────────────────────── */}
      {hasPending && (
        <section className="card p-4">
          <h3 className="font-bold mb-2 flex items-center gap-2">
            <Icon name="hourglass_top" size={18} className="text-amber-500" filled />
            {t('wallet.pending.title')}
          </h3>
          <div className="space-y-2">
            {pendingRecharges.map((r) => (
              <PendingRechargeRow key={`r-${r.id}`} row={r} t={t} onChanged={reloadAll} />
            ))}
            {pendingWithdrawals.map((w) => (
              <PendingWithdrawalRow key={`w-${w.id}`} row={w} t={t} />
            ))}
          </div>
        </section>
      )}

      {/* ─── Per-game history ─────────────────────────────────────── */}
      <section className="card p-4">
        <h3 className="font-bold mb-2 flex items-center gap-2">
          <Icon name="leaderboard" size={18} className="text-brand-500" />
          {t('wallet.history.byGame')}
        </h3>
        {summaryError && (
          <div className="text-xs bg-red-500/10 text-red-500 px-2 py-1 rounded">{summaryError}</div>
        )}
        {!summary && !summaryError && (
          <div className="text-sm text-neutral-500">{t('common.loading')}</div>
        )}
        {summary && summary.length === 0 && (
          <div className="text-sm text-neutral-500">{t('wallet.history.empty')}</div>
        )}
        {summary && summary.length > 0 && (
          <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {summary.map((g) => {
              const netPos = g.net > 0;
              const netNeg = g.net < 0;
              const netTone = netPos ? 'text-emerald-500'
                : netNeg ? 'text-red-500'
                : 'text-neutral-500';
              return (
                <div key={g.id} className="py-2.5 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{g.displayName}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      {t(
                        g.matches === 1
                          ? 'wallet.history.summary.one'
                          : 'wallet.history.summary.other',
                        { matches: g.matches, wins: fmt(g.wins), bets: fmt(g.bets) },
                      )}
                      {g.refunds > 0 && t('wallet.history.summary.refunded', { refunds: fmt(g.refunds) })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wide text-neutral-500">{t('wallet.net')}</div>
                    <div className={`font-bold tabular-nums ${netTone}`}>
                      {netPos ? '+' : netNeg ? '−' : ''}{fmt(Math.abs(g.net))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ─── Transaction ledger ───────────────────────────────────── */}
      <section className="card p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold flex items-center gap-2">
            <Icon name="receipt_long" size={18} className="text-brand-500" />
            {t('wallet.activity')}
          </h3>
          {ledger && ledger.total > LIMIT && (
            <div className="text-[11px] text-neutral-500">
              {t('wallet.activity.range', {
                from: offset + 1,
                to: Math.min(offset + LIMIT, ledger.total),
                total: ledger.total,
              })}
            </div>
          )}
        </div>

        {/* ─── 6-day rolling-window notice (Phase R7 follow-up) ───────
            We deliberately anchor the window to the player's MOST RECENT
            transaction, not "today" — a player who last played a week
            ago should still see their last 6 days of action, not an
            empty list. The banner shows the exact span so there's no
            confusion about what's being hidden. */}
        {ledger?.window && (
          <div className="mb-3 flex items-start gap-2 rounded-lg
                          bg-brand-50 dark:bg-brand-500/10
                          border border-brand-200 dark:border-brand-500/30
                          px-3 py-2 text-[11px] leading-snug
                          text-brand-700 dark:text-brand-200">
            <Icon name="info" size={14} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">
                {t('wallet.activity.window', { days: ledger.window.window_days })}
              </div>
              <div className="opacity-80">
                {new Date(ledger.window.cutoff_at).toLocaleDateString(undefined, {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}
                {' → '}
                {new Date(ledger.window.latest_at).toLocaleDateString(undefined, {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}
              </div>
            </div>
          </div>
        )}

        {ledgerError && (
          <div className="text-xs bg-red-500/10 text-red-500 px-2 py-1 rounded">{ledgerError}</div>
        )}
        {ledgerLoading && !ledger && (
          <div className="text-sm text-neutral-500">{t('common.loading')}</div>
        )}
        {ledger && ledger.rows.length === 0 && !ledgerLoading && (
          <div className="text-sm text-neutral-500">{t('wallet.activity.empty')}</div>
        )}

        {ledger && ledger.rows.length > 0 && (
          <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {ledger.rows.map((r) => {
              const baseMetaRaw = KIND_META[r.kind] || { tone: 'neutral' as const, icon: 'circle' };
              const baseMeta = { ...baseMetaRaw, label: t(`ledger.kind.${r.kind}`) || r.kind };
              // Withdrawal-hold UI swap: when the parent withdrawal
              // request has reached a terminal state, change the row's
              // label so it stops misleadingly saying "Withdrawal hold"
              // after the request was actually settled. Amount and
              // tone (negative — the coins did leave) stay as-is.
              let meta = baseMeta;
              let extraSubtext: string | null = null;
              let stateHasMovedOn = false;
              if (r.kind === 'withdrawal_hold' && r.hold_request_state) {
                if (r.hold_request_state === 'paid') {
                  const A = r.hold_approved_amount ?? r.hold_request_amount ?? 0;
                  const R = r.hold_request_amount ?? Math.abs(r.amount);
                  const partial = A != null && R != null && Number(A) < Number(R);
                  meta = {
                    label: t(partial ? 'ledger.withdrawalPaidPartial' : 'ledger.withdrawalPaid'),
                    tone: 'neg',
                    icon: 'check_circle',
                  };
                  if (r.hold_paid_transaction_id) {
                    extraSubtext = t('ledger.withdrawalPaid.txn', { txnId: r.hold_paid_transaction_id });
                  }
                  stateHasMovedOn = true;
                } else if (r.hold_request_state === 'rejected') {
                  meta = { label: t('ledger.withdrawalRejected'), tone: 'neutral', icon: 'cancel' };
                  extraSubtext = t('ledger.withdrawalRejected.note');
                  stateHasMovedOn = true;
                } else if (r.hold_request_state === 'expired') {
                  meta = { label: t('ledger.withdrawalExpired'), tone: 'neutral', icon: 'history_toggle_off' };
                  stateHasMovedOn = true;
                }
              }
              // Suppress the immutable submit-time note once the state
              // has moved on — extraSubtext now carries the useful info.
              const noteToShow = stateHasMovedOn ? null : r.note;
              // Cashback (turnover_reward) is the house-paid rebate — owner
              // spec 2026-07-10: it gets its OWN color (orange, both themes)
              // for the label, the [rate%] and the amount, so a player never
              // confuses it with winnings. The rate shown is the PLAYER'S
              // share Y% AT SETTLEMENT TIME, parsed from the row's own note
              // ("turnover return 1% (board) …") — historically accurate even
              // after the admin changes the config later.
              const isCashback = r.kind === 'turnover_reward';
              const cashbackPct = isCashback ? (String(r.note || '').match(/(\d+(?:\.\d+)?)%/)?.[1] ?? null) : null;
              const amountTone = isCashback ? 'text-orange-500'
                : r.amount > 0 ? 'text-emerald-500'
                : r.amount < 0 ? 'text-red-500'
                : 'text-neutral-500';
              const gameLabel = r.game && r.game !== 'platform'
                ? (summary?.find((g) => g.id === r.game)?.displayName || r.game)
                : null;
              return (
                <div key={r.id} className="py-2 flex items-start gap-3">
                  <Icon name={meta.icon} size={18} className="text-neutral-500 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${isCashback ? 'text-orange-500' : ''}`}>
                      {meta.label}{cashbackPct ? `[${cashbackPct}%]` : ''}
                      {gameLabel && (
                        <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-neutral-200 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">
                          {gameLabel}
                        </span>
                      )}
                    </div>
                    {extraSubtext && <div className="text-[11px] text-neutral-500 truncate">{extraSubtext}</div>}
                    {noteToShow && <div className="text-[11px] text-neutral-500 truncate">{noteToShow}</div>}
                    <div className="text-[11px] text-neutral-500">
                      {new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end gap-1">
                    <div className={`font-mono font-semibold tabular-nums ${amountTone}`}>
                      {r.kind === 'forfeit'
                        // Forfeit rows carry amount=0 by design (the
                        // matching bet row already booked the loss).
                        // Show an em-dash instead of "₹0" so it's
                        // clear no money moved at forfeit-time.
                        ? '—'
                        : <>{r.amount > 0 ? '+' : r.amount < 0 ? '−' : ''}{fmt(Math.abs(r.amount))}</>}
                    </div>
                    {r.balance_after != null && (
                      <div className="text-[10px] text-neutral-500 tabular-nums">
                        {t('wallet.activity.balanceAfter', { balance: fmt(r.balance_after) })}
                      </div>
                    )}
                    {/* Phase 18 — for a paid withdrawal ledger row, show
                        the "View screenshot" button so the player can
                        see the payout proof the agent/admin uploaded.
                        The viewer renders in three states (available /
                        expired / passive "awaiting proof from operator")
                        so the player always knows where they stand. */}
                    {r.kind === 'withdrawal_hold'
                      && r.hold_request_state === 'paid'
                      && r.hold_request_id != null && (() => {
                        const ps = proofState(
                          r.hold_payout_proof_uploaded_at || null,
                          r.hold_payout_proof_url || null,
                        );
                        return ps === 'available' || ps === 'expired' ? (
                          <ProofImageViewer state={ps}
                            endpoint={`/me/payments/withdrawals/${r.hold_request_id}/payout-proof`}
                            apiGet={(p) => api.get(p)} />
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold
                                           bg-neutral-100 text-neutral-500 ring-1 ring-inset ring-neutral-200
                                           dark:bg-neutral-700/50 dark:text-neutral-300 dark:ring-neutral-600/40">
                            {t('ledger.awaitingProof')}
                          </span>
                        );
                      })()}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {ledger && ledger.total > LIMIT && (
          <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-neutral-200 dark:border-neutral-800">
            <button
              className="btn-ghost text-sm"
              disabled={offset === 0 || ledgerLoading}
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            >
              <Icon name="chevron_left" size={16} />
              {t('wallet.newer')}
            </button>
            <button
              className="btn-ghost text-sm"
              disabled={offset + LIMIT >= ledger.total || ledgerLoading}
              onClick={() => setOffset(offset + LIMIT)}
            >
              {t('wallet.older')}
              <Icon name="chevron_right" size={16} />
            </button>
          </div>
        )}

        {/* Reach beyond the recent-6-day window into full history
            (2026-08-06 UX sweep). */}
        {ledger && !windowIsMax && (
          <div className="mt-2 text-center">
            <button className="btn-ghost text-sm" disabled={ledgerLoading} onClick={showOlder}>
              <Icon name="history" size={16} /> {t('wallet.showOlder')}
            </button>
          </div>
        )}
      </section>

      {/* Recharge + Withdraw flow modals */}
      <RechargeFlow open={rechargeOpen} onClose={() => setRechargeOpen(false)} onCompleted={reloadAll} />
      <WithdrawalFlow open={withdrawOpen} onClose={() => setWithdrawOpen(false)} onCompleted={reloadAll} />
    </div>
  );
}

// ─── Pending row helpers ───────────────────────────────────────────

// Phase 18 — client-side proof state computation. Mirrors the server's
// VIEW_EXPIRY_MS = 72h and UPLOAD_WINDOW_MS = 24h constants.
const VIEW_EXPIRY_MS   = 72 * 60 * 60 * 1000;
const UPLOAD_WINDOW_MS = 24 * 60 * 60 * 1000;
function proofState(uploadedAt: string | null, url: string | null): ProofState {
  if (!uploadedAt) return 'none';
  const age = Date.now() - new Date(uploadedAt).getTime();
  if (age > VIEW_EXPIRY_MS) return 'expired';
  return url ? 'available' : 'expired';
}
function canSenderUpload(createdAt: string, hasProof: boolean): boolean {
  if (hasProof) return false;
  return (Date.now() - new Date(createdAt).getTime()) < UPLOAD_WINDOW_MS;
}

function ProofUploadInlineRecharge({ rowId, onUploaded }: { rowId: number; onUploaded: () => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function send(blob: Blob) {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('proof', blob, 'screenshot.jpg');
      await api.postForm(`/me/recharge/${rowId}/proof`, fd);
      onUploaded();
    } catch (e: any) {
      setErr(e?.message || t('wallet.pending.upload.failed'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-1.5">
      <ProofImagePicker
        onFile={(b) => send(b)}
        onError={(m) => setErr(m)}
        disabled={busy}
        label={t('wallet.pending.upload.label')}
        hint={t('wallet.pending.upload.hint')}
      />
      {err && <div className="text-[11px] text-rose-700 dark:text-rose-300 mt-1">{err}</div>}
    </div>
  );
}

function PendingRechargeRow({ row, t, onChanged }: { row: RechargeRow; t: ReturnType<typeof useT>; onChanged: () => void }) {
  const senderState = proofState(row.proof_uploaded_at, row.proof_image_url);
  const showUpload  = canSenderUpload(row.created_at, senderState !== 'none');
  return (
    <div className="rounded-xl bg-amber-50 dark:bg-amber-400/10 border border-amber-300 dark:border-amber-400/30 p-3
                    space-y-2">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg grid place-items-center bg-amber-200/60 dark:bg-amber-400/20 text-amber-700 dark:text-amber-300">
          <Icon name="add_circle" size={18} filled />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-arena-800 dark:text-amber-100">
            {t('wallet.pending.recharge', { amount: '₹' + row.amount.toLocaleString('en-IN') })}
          </div>
          <div className="text-[11px] text-amber-700/80 dark:text-amber-200/70 font-mono break-all">
            {t('wallet.pending.txnPrefix')} {row.transaction_id}
          </div>
          <div className="text-[10px] text-amber-700/60 dark:text-amber-200/50">
            {new Date(row.created_at).toLocaleString()}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {/* Clock + label so even a non-reader catches at a glance that
              this request is still "ticking". The icon spins gently to
              hint at "in progress". */}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase
                           bg-amber-300/40 dark:bg-amber-400/20 text-amber-800 dark:text-amber-200 border border-amber-400/40">
            <Icon name="schedule" size={11} filled className="animate-pulse" />
            {t('wallet.state.pending')}
          </span>
          {senderState !== 'none' && (
            <ProofImageViewer state={senderState}
              endpoint={`/me/payments/recharges/${row.id}/proof`}
              apiGet={(p) => api.get(p)} />
          )}
        </div>
      </div>
      {showUpload && <ProofUploadInlineRecharge rowId={row.id} onUploaded={onChanged} />}
    </div>
  );
}

function PendingWithdrawalRow({ row, t }: { row: WithdrawalRow; t: ReturnType<typeof useT> }) {
  // Payout proof is uploaded by the approver (admin/agent), not the
  // player. We only RENDER the viewer here. The atomic 2-view counter
  // increment happens server-side on click.
  const payoutState = proofState(row.payout_proof_uploaded_at, row.payout_proof_url);
  return (
    <div className="rounded-xl bg-cyan-50 dark:bg-cyan-400/10 border border-cyan-300 dark:border-cyan-400/30 p-3
                    flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg grid place-items-center bg-cyan-200/60 dark:bg-cyan-400/20 text-cyan-700 dark:text-cyan-300">
        <Icon name="account_balance_wallet" size={18} filled />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-arena-800 dark:text-cyan-100">
          {t('wallet.pending.withdrawal', { amount: '₹' + row.amount.toLocaleString('en-IN') })}
        </div>
        <div className="text-[11px] text-cyan-700/80 dark:text-cyan-200/70 font-mono break-all">
          {row.upi_id}
        </div>
        <div className="text-[10px] text-cyan-700/60 dark:text-cyan-200/50">
          {new Date(row.created_at).toLocaleString()}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {/* Same icon-augmented pending chip as PendingRechargeRow.
            'paid' state swaps the clock for a green check so the
            visual difference between "still waiting" vs "money sent"
            is unmistakable. */}
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase
                         ${row.state === 'paid'
                           ? 'bg-emerald-300/40 dark:bg-emerald-400/20 text-emerald-800 dark:text-emerald-200 border border-emerald-400/40'
                           : 'bg-cyan-300/40 dark:bg-cyan-400/20 text-cyan-800 dark:text-cyan-200 border border-cyan-400/40'}`}>
          {row.state === 'paid'
            ? <><Icon name="check_circle" size={11} filled /> {t('wallet.state.paid')}</>
            : <><Icon name="schedule" size={11} filled className="animate-pulse" /> {t('wallet.state.pending')}</>}
        </span>
        {/* Phase 18 — payout proof viewer with 2-view limit. The
            ProofImageViewer hits the gated GET endpoint; backend
            increments view_count atomically and 410s on the 3rd open. */}
        {row.state === 'paid' && payoutState !== 'none' && (
          <ProofImageViewer state={payoutState}
            endpoint={`/me/payments/withdrawals/${row.id}/payout-proof`}
            apiGet={(p) => api.get(p)} />
        )}
      </div>
    </div>
  );
}
